import { LeadDto } from '@sr/dto';
import { Box, Button, Stack, Typography } from '@mui/material';
import { CardPaperTitled } from 'shared/ui/card-paper';
import { ErrorBanner } from 'shared/ui/error-banner';
import { useState } from 'react';
import MailIcon from '@mui/icons-material/Mail';
import { ViewEmailModal } from 'components/Companies/Feed/view-email-modal';
import AudioPlayer from '../../../entities/audio-player/audio-player';
import { Dialog } from 'shared/ui/dialog';
import { EmailPreview as EmailPreviewDetails, EmailPreviewData } from 'shared/ui/email-preview';
import { useContacts } from 'features/contact/contacts.hooks';
import { EntitiesWithContacts } from 'features/contact/contacts.types';

type AtsCallData = {
  direction: string;
  correspondentNumber: string;
  timeStamp: Date;
  ourNumber: string;
  employeeName?: string;
  duration: number;
  waitingTime: number;
  audioUrl: string;
};

const getTranscriptionStatusLabel = (transcriptionStatus?: string) => {
  if (transcriptionStatus === 'processing') return 'обрабатывается';
  if (transcriptionStatus === 'failed') return 'ошибка';
  return 'ожидание отправки';
};

const EmailPreview = ({ content, linkedEmails }: { content: string; linkedEmails: string[] }) => {
  try {
    const emailData: EmailPreviewData = JSON.parse(content);
    return <EmailPreviewDetails data={emailData} linkedEmails={linkedEmails} />;
  } catch (e) {
    console.log(e);
    return <ErrorBanner errorMessage="Ошибка предпросмотра емейла" />;
  }
};

const PhoneCallPreview = ({
  content,
  speechToTextEnabled,
  transcription,
  transcriptionStatus,
  onTextClick,
}: {
  content: string;
  speechToTextEnabled?: boolean;
  transcription?: string;
  transcriptionStatus?: string;
  onTextClick: () => void;
}) => {
  try {
    const callData: AtsCallData = JSON.parse(content);

    let direction;
    switch (callData.direction) {
      case 'in':
        direction = 'Входящий звонок';
        break;
      case 'out':
        direction = 'Исходящий звонок';
        break;
      case 'missed':
        direction = 'Пропущенный звонок';
        break;
      default:
        throw new Error('Unknown call direction');
    }

    const employeeLabel = callData.direction === 'out' ? 'Кто звонил' : 'Кто принял звонок';
    const employee = callData.employeeName ? `<p>${employeeLabel}: ${callData.employeeName}</p>` : '';
    const html = `<b>${direction} ${callData.duration} секунд.</b><br/><p>Время ожидания: ${callData.waitingTime} секунд.</p><p>Наш номер: ${callData.ourNumber}</p>${employee}<p>Номер собеседника: ${callData.correspondentNumber}</p>`;
    return (
      <>
        <Box dangerouslySetInnerHTML={{ __html: html }} />
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
          <AudioPlayer audioUrl={callData.audioUrl} />
          {speechToTextEnabled && (
            transcription ? (
              <Button variant="outlined" onClick={onTextClick}>
                Текст
              </Button>
            ) : (
              <Typography color="text.secondary" variant="body2">
                {getTranscriptionStatusLabel(transcriptionStatus)}
              </Typography>
            )
          )}
        </Stack>
      </>
    );
  } catch (e) {
    console.log(e);
    return <ErrorBanner errorMessage="Ошибка предпросмотра звонка АТС" />;
  }
};

const CallTextModal = ({ open, onClose, text }: { open: boolean; onClose: () => void; text: string }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <Dialog.Title>Транскрипция звонка</Dialog.Title>
    <Dialog.CloseButton onClick={onClose} />
    <Dialog.Content>
      <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</Box>
    </Dialog.Content>
  </Dialog>
);

export const LeadContentWidget = ({ lead }: { lead: LeadDto }) => {
  const [openEmailModal, setOpenEmailModal] = useState<boolean>(false);
  const [openCallTextModal, setOpenCallTextModal] = useState<boolean>(false);
  const { contacts } = useContacts({ relationId: lead.id, relationType: EntitiesWithContacts.lead });
  const linkedEmails = contacts?.linkedEmails.map(({ email }) => email) ?? [];
  return (
    <CardPaperTitled title="Содержание">
      {!lead.emailId && !lead.atsEntryId ? (
        <Box sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{lead.content}</Box>
      ) : lead.emailId ? (
        <EmailPreview content={lead.content} linkedEmails={linkedEmails} />
      ) : (
        <PhoneCallPreview
          content={lead.content}
          speechToTextEnabled={lead.speechToTextEnabled}
          transcription={lead.transcription}
          transcriptionStatus={lead.transcriptionStatus}
          onTextClick={() => setOpenCallTextModal(true)}
        />
      )}
      {lead.emailId && (
        <Button variant="outlined" onClick={() => setOpenEmailModal(true)} endIcon={<MailIcon />}>
          Email
        </Button>
      )}
      {lead.emailId && openEmailModal && <ViewEmailModal onClose={() => setOpenEmailModal(false)} open={openEmailModal} id={lead.emailId} leadId={lead.id} />}
      {lead.speechToTextEnabled && lead.atsEntryId && lead.transcription && openCallTextModal && (
        <CallTextModal open={openCallTextModal} onClose={() => setOpenCallTextModal(false)} text={lead.transcription} />
      )}
    </CardPaperTitled>
  );
};
